import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type { Filters } from "../types";
import { defaultFilters, isDefaultFilters } from "../types";
import { AssignedStateToggle } from "../../../../components/AssignedStateToggle";
import { DatePicker } from "../../../../components/DatePicker";
import { ImporterToggles } from "./ImporterToggles";

export type ImportFiltersProps = {
  value: Filters;
  onChange: (next: Filters) => void;
  importerOptions: string[];
};

export const ImportFilters = ({
  value,
  onChange,
  importerOptions,
}: ImportFiltersProps) => {
  const update = <K extends keyof Filters>(key: K, next: Filters[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip
            title={
              value.view === "deleted"
                ? "Showing deleted — tap to show active"
                : "Show deleted"
            }
          >
            <IconButton
              color={value.view === "deleted" ? "primary" : "default"}
              onClick={() =>
                update("view", value.view === "deleted" ? "active" : "deleted")
              }
              aria-label="toggle deleted imports"
              sx={{ p: 1, opacity: value.view === "deleted" ? 1 : 0.55 }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <AssignedStateToggle
            label="Warnings"
            icon={<WarningAmberRoundedIcon />}
            value={value.warnings}
            onChange={(v) => update("warnings", v)}
          />
        </Box>
        <ImporterToggles
          options={importerOptions}
          value={value.importers}
          onChange={(next) => update("importers", next)}
        />
        <TextField
          size="small"
          label="Importer or ID contains"
          value={value.namePattern}
          onChange={(e) => update("namePattern", e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <DatePicker
          mode="range"
          label="Date imported"
          value={{ from: value.from, to: value.to }}
          onChange={(next) =>
            onChange({ ...value, from: next.from, to: next.to })
          }
        />
        {!isDefaultFilters(value) && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={() => onChange({ ...defaultFilters, view: value.view })}
          >
            Clear
          </Button>
        )}
      </Box>
    </Paper>
  );
};
